import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Alert,
  CircularProgress,
} from '@mui/material';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import { apiService } from '../../services/apiService';

interface BudgetExportModalProps {
  open: boolean;
  onClose: () => void;
}

export const BudgetExportModal: React.FC<BudgetExportModalProps> = ({ open, onClose }) => {
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleExport = async () => {
    setError(null);

    if (!startDate || !endDate) {
      setError('Debe seleccionar ambas fechas');
      return;
    }

    if (startDate > endDate) {
      setError('La fecha inicio no puede ser mayor que la fecha fin');
      return;
    }

    setLoading(true);
    try {
      // Llamar al servicio para descargar el Excel
      const response = await apiService.budgets.exportExcel(startDate, endDate);
      
      // Crear blob y descargar
      const blob = new Blob([response], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      
      // Extraer nombre de archivo del header Content-Disposition si está disponible
      const filename = `presupuestos_${startDate}_a_${endDate}.xlsx`;
      a.download = filename;
      
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      // Cerrar modal después de descargar
      handleClose();
    } catch (err: any) {
      setError(err.message || 'Error al descargar el archivo');
      console.error('Error exportando Excel:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setStartDate('');
    setEndDate('');
    setError(null);
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Exportar Presupuestos a Excel</DialogTitle>
      <DialogContent sx={{ pt: 2 }}>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <TextField
            label="Fecha Inicio"
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            fullWidth
          />
          <TextField
            label="Fecha Fin"
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            fullWidth
          />
          <Alert severity="info">
            Se descargarán todos los presupuestos modificados en el rango de fechas seleccionado con las columnas: 
            Proyecto, Cliente, Vendedor, Versión, Fecha, Estado, Total USD, Total ARS
          </Alert>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>
          Cancelar
        </Button>
        <Button
          onClick={handleExport}
          variant="contained"
          disabled={loading || !startDate || !endDate}
          startIcon={loading ? <CircularProgress size={20} /> : <FileDownloadIcon />}
        >
          {loading ? 'Descargando...' : 'Descargar Excel'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
